import { observable, action } from "mobx";
import makeApi, { URLS } from "Utils/apiURL";
import { toasterState } from "Components/common/toaster";
import { RATE_WIDGET_IDS, CATEGORY_ENDPOINT_MAP } from "../utils/widgetConstants";
import { resolveDatePreset } from "../utils/datePresetUtils";
import { resolveChartData } from "../utils/chartDataUtils";

const LATEST_REPLIES_WIDGET_ID = "LATEST_REPLIES";

class WidgetDataStore {
  @observable widgetData = {};
  @observable loadingByCategory = {};
  @observable activeDateFilter = "7d";
  // Custom-date-filter: {fromDate, toDate} in ms; used only when activeDateFilter === "custom".
  // In-memory only (survives dashboard switches, resets on full page reload) — matches the presets.
  @observable activeCustomRange = null;
  @observable activeMemberIds = [];

  @action
  setDateFilter = preset => {
    this.activeDateFilter = preset;
  };

  // Custom-date-filter: apply a picked range and switch the active filter to "custom".
  @action
  setCustomDateRange = range => {
    if (!range || range.fromDate == null || range.toDate == null) return;
    this.activeCustomRange = { fromDate: range.fromDate, toDate: range.toDate };
    this.activeDateFilter = "custom";
  };

  // Custom-date-filter: single source of truth for {fromDate, toDate} — custom range when active,
  // otherwise the preset's resolved range.
  resolveActiveRange = () => {
    if (this.activeDateFilter === "custom" && this.activeCustomRange) {
      return { fromDate: this.activeCustomRange.fromDate, toDate: this.activeCustomRange.toDate };
    }
    return resolveDatePreset(this.activeDateFilter);
  };

  @action
  setMemberIds = ids => {
    this.activeMemberIds = ids || [];
  };

  @action
  setLoadingForCategory = (category, value) => {
    this.loadingByCategory = { ...this.loadingByCategory, [category]: value };
  };

  @action
  setWidgetData = (widgetId, data) => {
    this.widgetData = { ...this.widgetData, [widgetId]: data };
  };

  getWidgetData = widgetId => {
    return this.widgetData[widgetId] || null;
  };

  isCategoryLoading = category => {
    return !!this.loadingByCategory[category];
  };

  @action
  clearWidgetData = widgetId => {
    const updated = { ...this.widgetData };
    delete updated[widgetId];
    this.widgetData = updated;
  };

  fetchSingleWidget = async (widget, dashboardId) => {
    if (!widget?.widgetId) return;
    const { fromDate, toDate } = this.resolveActiveRange();
    const memberIds = this.activeMemberIds;
    if (widget.widgetId === LATEST_REPLIES_WIDGET_ID) {
      this.setLoadingForCategory(LATEST_REPLIES_WIDGET_ID, true);
      await this.fetchLatestReplies(fromDate, toDate, memberIds);
    } else if (RATE_WIDGET_IDS.has(widget.widgetId)) {
      this.setLoadingForCategory(widget.widgetId, true);
      await this.fetchRateSeriesData(widget.widgetId, fromDate, toDate, memberIds, dashboardId);
    } else {
      this.setLoadingForCategory(widget.category, true);
      // Preserve the widget's persisted "Breakdown by member" state on single re-fetches (e.g. after
      // an Audience Scope change) so the "<key>ByMember" data isn't dropped.
      await this.fetchCategoryData(
        widget.category, [widget.widgetId], fromDate, toDate, memberIds, dashboardId, !!widget.memberBreakdown
      );
    }
  };

  // SG-2164: re-fetches a single widget's data with memberBreakdown requested, so its "<key>ByMember"
  // rows become available once the user turns on the "Breakdown by member" DotMenu option.
  fetchWidgetMemberBreakdown = async (widget, dashboardId) => {
    if (!widget?.widgetId) return;
    const { fromDate, toDate } = this.resolveActiveRange();
    await this.fetchCategoryData(widget.category, [widget.widgetId], fromDate, toDate, this.activeMemberIds, dashboardId, true);
  };

  @action
  fetchAllWidgetData = async (widgets, dashboardId) => {
    if (!widgets?.length) return;

    const { fromDate, toDate } = this.resolveActiveRange();
    const memberIds = this.activeMemberIds;

    // SG-2164: split category widgets into normal vs member-breakdown batches so persisted
    // "Breakdown by member" widgets load their "<key>ByMember" data on the initial page load too
    // (memberBreakdown is a per-request flag applied to every widgetId in the call).
    const categoryMap = {};
    const categoryBreakdownMap = {};
    const rateWidgets = [];
    let hasLatestRepliesWidget = false;

    widgets.forEach(w => {
      if (w.widgetId === LATEST_REPLIES_WIDGET_ID) {
        hasLatestRepliesWidget = true;
        this.setLoadingForCategory(LATEST_REPLIES_WIDGET_ID, true);
      } else if (RATE_WIDGET_IDS.has(w.widgetId)) {
        rateWidgets.push(w);
        this.setLoadingForCategory(w.widgetId, true);
      } else {
        const target = w.memberBreakdown ? categoryBreakdownMap : categoryMap;
        if (!target[w.category]) target[w.category] = [];
        target[w.category].push(w.widgetId);
      }
    });

    Object.keys(categoryMap).forEach(cat => this.setLoadingForCategory(cat, true));
    Object.keys(categoryBreakdownMap).forEach(cat => this.setLoadingForCategory(cat, true));

    const categoryFetches = [
      ...Object.entries(categoryMap).map(([category, widgetIds]) =>
        this.fetchCategoryData(category, widgetIds, fromDate, toDate, memberIds, dashboardId, false)
      ),
      ...Object.entries(categoryBreakdownMap).map(([category, widgetIds]) =>
        this.fetchCategoryData(category, widgetIds, fromDate, toDate, memberIds, dashboardId, true)
      )
    ];

    const rateFetches = rateWidgets.map(w =>
      this.fetchRateSeriesData(w.widgetId, fromDate, toDate, memberIds, dashboardId)
    );

    const latestRepliesFetch = hasLatestRepliesWidget
      ? [this.fetchLatestReplies(fromDate, toDate, memberIds)]
      : [];

    await Promise.all([...categoryFetches, ...rateFetches, ...latestRepliesFetch]);
  };

  // SG-2164: dashboardId is threaded through so the backend can resolve each requested widget's
  // persisted Audience Scope (Everyone/Specific Users/Specific Teams) by widgetId. Optional/nullable —
  // existing callers that don't pass it just keep getting today's global-filter-only behavior.
  // memberBreakdown asks the backend to also include a "<key>ByMember" array in the response.
  fetchCategoryData = async (category, widgetIds, fromDate, toDate, memberIds, dashboardId, memberBreakdown = false) => {
    const urlKey = CATEGORY_ENDPOINT_MAP[category];
    if (!urlKey || !URLS[urlKey]) return;

    try {
      const res = await makeApi({
        url: URLS[urlKey],
        method: "POST",
        data: { widgetIds, fromDate, toDate, memberIds, dashboardId, memberBreakdown }
      });
      const data = res?.data;
      if (data?.status === "failed") {
        console.error(`DashboardV2: fetchCategoryData(${category}) failed`, data?.error);
        toasterState.setToastMsg(`Failed to load ${category.toLowerCase()} widget data`, "fail");
        return;
      }
      if (data) {
        widgetIds.forEach(widgetId => {
          this.setWidgetData(widgetId, { ...data, _category: category });
        });
      }
    } catch (err) {
      console.error(`DashboardV2: fetchCategoryData(${category}) error`, err);
      toasterState.setToastMsg(`Failed to load ${category.toLowerCase()} widget data`, "fail");
    } finally {
      this.setLoadingForCategory(category, false);
    }
  };

  fetchLatestReplies = async (fromDate, toDate, memberIds) => {
    try {
      const res = await makeApi({
        url: URLS.outbox,
        method: "POST",
        data: {
          pageNo: 1,
          limit: 5,
          sortDirection: "DESC",
          sortParam: "REPLY_DATE",
          filters: {
            memberIds: memberIds || [],
            filterKey: "REPLY_COUNT",
            channel: ["EMAIL", "LINKEDIN"],
            replied: { fromDate, toDate },
            statuses: [],
            all: false
          }
        }
      });

      const data = res?.data;
      this.setWidgetData(LATEST_REPLIES_WIDGET_ID, { replies: data?.result || [] });
    } catch (err) {
      console.error("DashboardV2: fetchLatestReplies error", err);
      this.setWidgetData(LATEST_REPLIES_WIDGET_ID, { replies: [] });
    } finally {
      this.setLoadingForCategory(LATEST_REPLIES_WIDGET_ID, false);
    }
  };

  fetchRateSeriesData = async (widgetId, fromDate, toDate, memberIds, dashboardId) => {
    try {
      const res = await makeApi({
        url: URLS.getDashboardWidgetsRateSeries,
        method: "POST",
        // SG-2164: dashboardId lets the backend resolve this rate widget's per-widget Audience Scope.
        data: { widgetId, fromDate, toDate, memberIds, dashboardId }
      });
      const data = res?.data;
      if (data?.status === "failed") {
        console.error(`DashboardV2: fetchRateSeriesData(${widgetId}) failed`, data?.error);
        toasterState.setToastMsg(data?.error || "Failed to load rate widget data", "fail");
        return;
      }
      if (data) {
        const chartData = resolveChartData(data, this.activeDateFilter, this.activeCustomRange);
        this.setWidgetData(widgetId, {
          currentRate: data.currentRate,
          previousRate: data.previousRate,
          trend: data.trend,
          chartData,
          series: data.series || [],
          _isRate: true
        });
      }
    } catch (err) {
      console.error(`DashboardV2: fetchRateSeriesData(${widgetId}) error`, err);
      toasterState.setToastMsg("Failed to load rate widget data", "fail");
    } finally {
      this.setLoadingForCategory(widgetId, false);
    }
  };
}

const widgetDataStore = new WidgetDataStore();
export { widgetDataStore };
export default widgetDataStore;
